import { useMemo } from 'react';
import { useSetupStatus } from './useSetupStatus';
import type { EtapaStatus } from './useSetupStatus';

// ═══════════════════════════════════════════════
// Tipos de Badge para o Sidebar
// ═══════════════════════════════════════════════

export interface SidebarBadge {
  type: 'warning' | 'done' | 'locked' | 'next' | 'none';
  count?: number;
  tooltip?: string;
}

// ═══════════════════════════════════════════════
// Hook de Badges para o Sidebar
// ═══════════════════════════════════════════════

export function useSetupBadges(opts?: { routePrefix?: string; cargo?: string }): Record<string, SidebarBadge> {
  const { etapas, isLoading } = useSetupStatus({
    routePrefix: opts?.routePrefix,
    cargo: opts?.cargo,
  });

  return useMemo(() => {
    if (isLoading) return {};

    // Identificar a proxima etapa (primeira nao-concluida e nao-bloqueada)
    const proximaEtapa = etapas.find(
      (e) => e.status === 'pendente' || e.status === 'parcial'
    );

    const badges: Record<string, SidebarBadge> = {};

    for (const etapa of etapas) {
      const isProximo = proximaEtapa?.id === etapa.id;

      switch (etapa.status) {
        case 'concluida':
          break;

        case 'bloqueada':
          badges[etapa.id] = {
            type: 'locked',
            tooltip: etapa.bloqueadoPor
              ? `Conclua "${etapa.bloqueadoPor}" antes desta etapa`
              : 'Etapa anterior pendente',
          };
          break;

        case 'parcial':
          badges[etapa.id] = {
            type: 'warning',
            count: etapa.meta - etapa.total,
            tooltip: `${etapa.total} de ${etapa.meta} completo(s)`,
          };
          break;

        case 'pendente':
          if (isProximo) {
            badges[etapa.id] = {
              type: 'next',
              tooltip: 'Proximo passo',
            };
          } else {
            badges[etapa.id] = {
              type: 'warning',
              count: 0,
              tooltip: `Nenhum ${etapa.nome.toLowerCase()} cadastrado`,
            };
          }
          break;

        default:
          badges[etapa.id] = { type: 'none' };
      }
    }

    // Módulos operacionais — sem badge de conclusão no menu
    badges.dispositivos = { type: 'none' };

    return badges;
  }, [etapas, isLoading]);
}
